import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import _ from "lodash";
import moment from "moment";
import {
  makeStyles,
  IconButton,
  InputAdornment,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  TablePagination,
  Checkbox,
  Box,
  CircularProgress,
} from "@material-ui/core";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import Card from "../../components/Card/Card";
import CardHeader from "../../components/Card/CardHeader";
import CardBody from "../../components/Card/CardBody";
import CustomInput from "../../components/CustomInput/CustomInput";
import VisibilityIcon from "@material-ui/icons/Visibility";
import People from "@material-ui/icons/People";
import DeleteIcon from "@material-ui/icons/Delete";
import { Close, Search } from "@material-ui/icons";
import AccountVerificationView from "./AccountVerificationView";
import { baseUrl, GetAllAccountsAPI } from "../../config/api";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";

const useStyles = makeStyles((theme) => ({
  fab: {
    position: "absolute",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0",
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF",
    },
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1",
    },
  },
}));

const columns = [
  {
    id: "name",
    label: "Name",
    format: (value) => (value ? value : "NA"),
  },
  {
    id: "accountCode",
    label: "Account Code",
    format: (value) => (value ? value : "NA"),
  },
  { id: "whatsappNumber", label: "whatsapp Number", },
  { id: "city", label: "City" },
  { id: "state", label: "State" },
  {
    id: "total",
    label: "Total Users",
    format: (value) => (value ? value : "NA"),
  },

  { id: "action", label: "Action" },
];

export default function AccountVerificationList(props) {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState("");
  const [search, setSearch] = useState(""); // For debounced search input
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openViewDialog, setOpenViewDialog] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, accountId: null });
  const authToken = useSelector((state) => state.userReducer.authToken);
  const userInfo = useSelector((state) => state.userReducer.userInfo);

  const hasInitialLoad = useRef(false);
  const isLoadingRef = useRef(false);

  const _getAccounts = useCallback(async (pageNum, rowsPerPageNum, search = "") => {
    // Prevent duplicate calls
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      const skip = pageNum * rowsPerPageNum;
      let query = `&limit=${rowsPerPageNum}&skip=${skip}&adminVerification=false`;

      if (search) {
        query += `&search=${encodeURIComponent(search)}`;
      }

      const res = await axios.get(
        GetAllAccountsAPI(userInfo.entity) + query,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setAccounts(res.data.accounts || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Something went wrong, Please Contact Admin", {
        position: "top-center",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setAccounts([]);
      setTotal(0);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [userInfo.entity, authToken]);

  // Initial load: only on mount
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      await _getAccounts(0, rowsPerPage, "");
      if (isMounted) {
        hasInitialLoad.current = true;
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Refresh when view dialog closes
  useEffect(() => {
    if (hasInitialLoad.current && !openViewDialog) {
      _getAccounts(page, rowsPerPage, searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openViewDialog]);

  // Debounced search effect: only runs when search input changes (not on initial mount)
  useEffect(() => {
    // Skip on initial mount to prevent duplicate call
    if (!hasInitialLoad.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setSearchTerm(search);
      setPage(0); // Reset to first page on new search
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [search]);

  // Fetch when searchTerm changes (after debounce) OR when pagination changes
  useEffect(() => {
    // Skip initial mount (already handled above)
    if (!hasInitialLoad.current) {
      return;
    }
    _getAccounts(page, rowsPerPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm]); // Pagination and search changes trigger this

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  function closeViewDialog() {
    setOpenViewDialog(null);
  }

  // Refresh accounts function
  const refreshAccounts = useCallback(() => {
    _getAccounts(page, rowsPerPage, searchTerm);
  }, [page, rowsPerPage, searchTerm, _getAccounts]);

  // Use accounts directly (no client-side filtering)
  let data = accounts;

  const handleDelete = async () => {
    try {
      const response = await axios.delete(`${baseUrl}/accounts/${deleteDialog.accountId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.status === 200) {
        toast.success("Account deleted successfully");
        refreshAccounts(); // Refresh accounts after successful delete
      } else {
        toast.error("Failed to delete account");
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("An error occurred while deleting the account");
    } finally {
      setDeleteDialog({ open: false, accountId: null });
    }
  };

  return (
    <>
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, accountId: null })}
        aria-labelledby="delete-confirmation-dialog"
      >
        <DialogTitle id="delete-confirmation-dialog">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this account? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, accountId: null })} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="red" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="primary">
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h4 className={classes.cardTitleWhite}>Account Verification</h4>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-evenly",
                    alignItems: "center",
                  }}
                >
                  <CustomInput
                    id="search"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      style: {
                        marginTop: 0,
                        marginRight: 10,
                        color: "white",
                      },
                      placeholder: "Search",
                      value: search,
                      onChange: (e) => setSearch(e.target.value),
                      endAdornment: (
                        <InputAdornment position="end">
                          {searchTerm ? (
                            <IconButton
                              color={"secondary"}
                              onClick={() => {
                                setSearch("");
                                setSearchTerm("");
                              }}
                            >
                              <Close />
                            </IconButton>
                          ) : (
                            <IconButton color={"secondary"}>
                              <Search />
                            </IconButton>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </div>
            </CardHeader>

            <CardBody>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <Checkbox
                      style={{
                        color: "#512da8",
                      }}
                      onChange={(e) => {
                        let c = [];
                        if (e.target.checked) {
                          data.map((i) => {
                            c.push(i._id);
                          });
                        }
                        setSelectedRows(c);
                      }}
                      checked={
                        selectedRows.length > 0 &&
                        data.length === selectedRows.length
                      }
                      inputProps={{ "aria-labelledby": -1 }}
                    />
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={"center"}
                        style={{ minWidth: column.minWidth }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} align="center" style={{ padding: "40px" }}>
                        <CircularProgress />
                        <Box style={{ color: "#666", marginTop: "16px" }}>
                          {searchTerm ? "Searching accounts..." : "Loading accounts..."}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} align="center" style={{ padding: "40px" }}>
                        <Box style={{ color: "#666" }}>
                          {searchTerm ? "No accounts found matching your search." : "No accounts found."}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row) => {
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row.code}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              style={{
                                color: "#512da8",
                              }}
                              onChange={() => {
                                let copy = _.clone(selectedRows);
                                if (_.includes(copy, row._id)) {
                                  let index = _.indexOf(copy, row._id);
                                  copy.splice(index, 1);
                                } else {
                                  copy.push(row._id);
                                }
                                setSelectedRows(copy);
                              }}
                              checked={_.includes(selectedRows, row._id)}
                              inputProps={{ "aria-labelledby": row._id }}
                            />
                          </TableCell>
                          {columns.map((column) => {
                            const value = row[column.id];

                            if (column.id === "total") {
                              return (
                                <TableCell align={"center"}>
                                  {row.users?.length || 0}
                                </TableCell>
                              );
                            }

                            if (column.id === "action") {
                              return (
                                <TableCell align={"center"}>
                                  <Box display={"flex"} justifyContent={"center"}>
                                    <IconButton
                                      onClick={() =>
                                        setOpenViewDialog({
                                          open: true,
                                          item: row,
                                        })
                                      }
                                      aria-label="view"
                                    >
                                      <VisibilityIcon />
                                    </IconButton>
                                    <IconButton
                                      onClick={() =>
                                        setDeleteDialog({ open: true, accountId: row._id })
                                      }
                                      aria-label="delete"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              );
                            }
                            return (
                              <TableCell key={column.id} align={"center"}>
                                {column.format ? column.format(value) : value}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </CardBody>
          </Card>
        </GridItem>
        {openViewDialog ? (
          <AccountVerificationView
            open={openViewDialog.open}
            close={closeViewDialog}
            item={openViewDialog.item}
          />
        ) : null}
      </GridContainer>
    </>
  );
}
